import PropTypes from "prop-types";
import React, { useRef } from "react";

import styled from "styled-components/macro";

import { useRespondent } from "Signups/stateHooks";
import { AnimatePresence } from "framer-motion";

import { SquareCheck } from "UI/FnrEnrichedCheckbox";
import { colors } from "UI/colors";

import DeleteUserButton from "./DeleteUserButton";
import PaymentStatusPill from "./PaymentStatusPill";
import RespondentEntryExtendedFields from "./RespondentEntryExtendedFields";

RespondentEntryNonUser.propTypes = {
  toggleIsChecked: PropTypes.func,
  updateName: PropTypes.func,
  deleteRespondent: PropTypes.func,
  userExtendedFields: PropTypes.array,
};

export default function RespondentEntryNonUser({
  person,
  setChecked,
  updateName,
  deleteRespondent,
  signupItem,
  userExtendedFields,
}) {
  const { userID, isChecked, name, additionalNameID } = person;
  const isEditable = !additionalNameID; // additionalNames only have an additionalNameID when coming from an existing signup.
  const inputRef = useRef();
  const [, setRespondent] = useRespondent(
    signupItem.ID,
    additionalNameID || userID
  );

  const isPaid =
    person.paymentStatus === "Paid" || person.paymentStatus === "MarkedAsPaid";
  const isUnclickable = isPaid && isChecked;

  const handleClick = () => {
    if (isUnclickable) return;
    setChecked(userID, !isChecked);
  };

  const handleNameChange = ({ target: { value } }) => {
    updateName(userID, value);
    // persist this in context so that resetRespondents does not delete it and leaves checked.
    setRespondent({
      ...person,
      name: value,
    });
  };

  const handleDeleteClick = () => {
    deleteRespondent(userID);
  };

  const handleEnterPress = (e) => {
    e.preventDefault();
    inputRef.current?.blur();
  };

  return (
    <>
      <Wrapper>
        <CheckboxWrapper
          onClick={handleClick}
          role="button"
          isUnclickable={isUnclickable}
        >
          <SquareCheck checked={isChecked} circle color="#278dc1" />
        </CheckboxWrapper>

        {isEditable ? (
          <Form onSubmit={handleEnterPress}>
            <Input
              ref={inputRef}
              value={person.name}
              onChange={handleNameChange}
              placeholder="Enter a name..."
              autoFocus
            />
          </Form>
        ) : (
          <>
            <Name>{name}</Name>
            <PaymentStatusPill
              paymentStatus={person.paymentStatus}
              signupItem={signupItem}
            />
          </>
        )}
        {!isPaid ? <DeleteUserButton onClick={handleDeleteClick} /> : null}
      </Wrapper>
      <AnimatePresence>
        {isChecked && userExtendedFields.length > 0 && (
          <ExtendedFieldsWrapper>
            <RespondentEntryExtendedFields
              respondent={person}
              fields={userExtendedFields}
              signupItem={signupItem}
            />
          </ExtendedFieldsWrapper>
        )}
      </AnimatePresence>
    </>
  );
}

const Wrapper = styled.div`
  margin: 10px;
  display: flex;
  flex-shrink: 0;
  align-items: center;
  min-height: 30px;
  font-weight: 400;
`;

const CheckboxWrapper = styled.div`
  cursor: ${({ isUnclickable }) => (isUnclickable ? "not-allowed" : "pointer")};
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  position: relative;
  font-size: 24px;
  margin-right: 8px;
`;

const Form = styled.form`
  flex: 1;
`;

const Input = styled.input`
  display: block;
  box-sizing: border-box;
  width: 100%;
  border: 1px solid #e3e3e3;
  font-size: 16px;
  padding: 10px 6px 8px;
  border-radius: 6px;
  outline: none;
  min-width: 100px;

  &::placeholder {
    color: ${colors.fnBlue};
    opacity: 0.8;
    font-style: italic;
  }

  &:focus {
    border: 1px solid ${colors.fnBlue};
  }
`;

const Name = styled.div`
  margin-left: 4px;
`;

const ExtendedFieldsWrapper = styled.div`
  padding: 0 13px;
`;
